import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';

import { api } from '../api.js';
import { relativeTime } from '../lib/format.js';
import { useT } from '../lib/i18n.js';
import { CloseIcon } from './icons.jsx';
import { EmptyState, ErrorNote, Loading } from './ui.jsx';

/**
 * The bell in the sidebar header (client area and admin area) with its unread badge ("9+" past
 * nine). It opens the latest notifications in a popover under the bell, a sheet across the bottom
 * on phones; Escape or a click outside closes it and focus goes back to the bell. Following a
 * notification marks it read. `realm`: 'client' or 'staff'.
 *
 * The count is polled every minute with `x-octbill-background`, so it never keeps an idle staff
 * session alive (Settings → Security → admin idle timeout).
 */
const PATHS = { client: '/api/client/notifications', staff: '/api/admin/account/notifications' };
const POLL_MS = 60_000;
const LIMIT = 10;
const BACKGROUND = { 'x-octbill-background': '1' };

export function badgeText(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '';
  return n > 9 ? '9+' : String(n);
}

const TONE_DOT = { info: 'bg-accent', ok: 'bg-kelp', warn: 'bg-amber', danger: 'bg-danger' };
const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const WIDTH = 384;

function useFeed(realm, { onlyUnread = false, limit = 1, enabled = true } = {}) {
  return useQuery({
    queryKey: ['notifications', realm, onlyUnread, limit],
    queryFn: () => api(`${PATHS[realm]}?limit=${limit}${onlyUnread ? '&unread=1' : ''}`, { headers: BACKGROUND }),
    refetchInterval: limit === 1 ? POLL_MS : false,
    refetchIntervalInBackground: false,
    // Switching All / Unread keeps the list on screen until the other one arrives.
    placeholderData: (previous) => previous,
    enabled,
    retry: false,
  });
}

function Popover({ id, realm, anchor, unread: polled = 0, onClose }) {
  const t = useT();
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [place, setPlace] = useState(null);
  const panel = useRef(null);
  const headingId = useId();
  const qc = useQueryClient();
  const feed = useFeed(realm, { onlyUnread, limit: LIMIT });
  const mark = useMutation({
    mutationFn: (uuid) => api(`${PATHS[realm]}/read`, { method: 'POST', body: uuid ? { uuid } : {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', realm] }),
  });
  const items = feed.data?.data ?? [];
  const unread = feed.data?.meta.unread ?? polled;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // Wider screens: under the bell, kept on screen. Phones get the bottom sheet from the classes.
  useLayoutEffect(() => {
    const measure = () => {
      const r = anchor.current?.getBoundingClientRect();
      const desktop = window.matchMedia?.('(min-width: 768px)').matches;
      if (!r || !desktop) return setPlace(null);
      setPlace({ top: r.bottom + 8, left: Math.max(16, Math.min(r.left, window.innerWidth - WIDTH - 16)) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [anchor]);

  useEffect(() => {
    panel.current?.focus();
    const onDown = (e) => {
      if (!panel.current?.contains(e.target) && !anchor.current?.contains(e.target)) closeRef.current(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [anchor]);

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = [...panel.current.querySelectorAll(FOCUSABLE)];
    if (!nodes.length) return;
    const [first, last] = [nodes[0], nodes.at(-1)];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const filterClass = (on) =>
    `rounded-full border px-2.5 py-0.5 text-xs font-semibold ${on ? 'border-accent bg-accent/10 text-text' : 'border-line text-muted hover:text-text'}`;

  return createPortal(
    <>
      <div aria-hidden="true" className="fixed inset-0 z-40 bg-black/40 md:hidden" />
      <div
        ref={panel}
        id={id}
        role="dialog"
        aria-labelledby={headingId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={place ? { top: place.top, left: place.left, width: WIDTH } : undefined}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-2xl border border-line bg-surface text-text shadow-xl outline-none md:inset-x-auto md:bottom-auto md:max-h-[min(34rem,calc(100dvh-5rem))] md:rounded-[10px]"
      >
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 id={headingId} className="text-base font-semibold">
            {t('bell.title')}
          </h2>
          <span className="flex items-center gap-1">
            {unread ? (
              <button
                type="button"
                className="rounded-md px-2 py-1 text-sm font-semibold text-accent hover:bg-sunk"
                aria-busy={mark.isPending || undefined}
                onClick={() => mark.mutate(null)}
              >
                {t('bell.mark_all')}
              </button>
            ) : null}
            <button
              type="button"
              aria-label={t('bell.close')}
              className="rounded-md p-1.5 text-muted hover:bg-sunk hover:text-text"
              onClick={() => onClose()}
            >
              <CloseIcon />
            </button>
          </span>
        </div>
        <div className="flex gap-2 px-4 pt-3" role="group" aria-label={t('bell.show')}>
          <button
            type="button"
            aria-pressed={!onlyUnread}
            className={filterClass(!onlyUnread)}
            onClick={() => setOnlyUnread(false)}
          >
            {t('bell.all')}
          </button>
          <button
            type="button"
            aria-pressed={onlyUnread}
            className={filterClass(onlyUnread)}
            onClick={() => setOnlyUnread(true)}
          >
            {unread ? t('bell.unread_count', { count: unread }) : t('bell.unread')}
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
          {feed.isPending ? <Loading inline /> : null}
          <div className="px-2">
            <ErrorNote error={feed.error ?? mark.error} />
          </div>
          {feed.data && !items.length ? (
            <div className="px-2">
              <EmptyState compact title={onlyUnread ? t('bell.none_unread') : t('bell.empty')} />
            </div>
          ) : null}
          {items.length ? (
            <ul className="flex flex-col">
              {items.map((n) => {
                const content = (
                  <>
                    <span
                      aria-hidden="true"
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${n.read ? 'bg-line-strong' : (TONE_DOT[n.tone] ?? TONE_DOT.info)}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm ${n.read ? '' : 'font-semibold'}`}>
                        {n.title}
                        {n.read ? null : <span className="sr-only"> {t('bell.unread_mark')}</span>}
                      </span>
                      {n.body ? <span className="mt-0.5 block text-sm break-words text-muted">{n.body}</span> : null}
                      <span className="mt-0.5 block text-xs text-muted">{relativeTime(n.created_at)}</span>
                    </span>
                  </>
                );
                const cls = 'flex w-full gap-3 rounded-md px-2 py-2 text-left hover:bg-sunk focus-visible:bg-sunk';
                return (
                  <li key={n.uuid}>
                    {n.url ? (
                      <Link
                        to={n.url}
                        className={cls}
                        onClick={() => {
                          if (!n.read) mark.mutate(n.uuid);
                          onClose(false);
                        }}
                      >
                        {content}
                      </Link>
                    ) : (
                      <button type="button" className={cls} onClick={() => !n.read && mark.mutate(n.uuid)}>
                        {content}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      </div>
    </>,
    document.body,
  );
}

export function NotificationBell({ realm = 'client', className = '' }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const button = useRef(null);
  const panelId = useId();
  const { pathname } = useLocation();
  const count = useFeed(realm);
  const unread = count.data?.meta.unread ?? 0;
  const badge = badgeText(unread);

  // A followed link closes the popover.
  useEffect(() => setOpen(false), [pathname]);

  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };

  return (
    <>
      <button
        ref={button}
        type="button"
        className={`relative inline-flex rounded-md p-1.5 text-white/80 hover:text-white aria-expanded:text-white ${className}`}
        aria-label={unread ? t('bell.label_unread', { count: unread }) : t('bell.title')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <svg
          viewBox="0 0 24 24"
          className="size-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2h-15L6 16Z" strokeLinejoin="round" />
          <path d="M10 20a2 2 0 0 0 4 0" strokeLinecap="round" />
        </svg>
        {badge ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-accent px-1 text-center text-[10px] leading-4 font-bold text-on-accent">
            {badge}
          </span>
        ) : null}
      </button>
      {open ? <Popover id={panelId} realm={realm} anchor={button} unread={unread} onClose={close} /> : null}
    </>
  );
}
